import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowUpRight } from "lucide-react";
import { PageHero } from "@/components/page-parts";
import { LOCAL_AREAS, localReadingPosts } from "@/lib/local-content";
import coastImage from "@/assets/golden-beach-hero.jpg";

const url = "https://coastsidelaw.com.au/local-guides";
const guides = [
  { slug: "joint-tenants-vs-tenants-in-common-queensland", label: "Buying together: choosing ownership" },
  { slug: "unapproved-building-work-decks-sheds-pools-queensland", label: "Renovations and approval records" },
  { slug: "when-to-update-your-will-queensland", label: "When to review your will" },
  { slug: "how-to-apply-for-probate-in-queensland", label: "Probate: steps, FAQs and local examples" },
  { slug: "buying-or-selling-a-business-in-queensland", label: "Buying or selling a Queensland business" },
  { slug: "assigning-a-retail-shop-lease-queensland", label: "Assigning a retail shop lease" },
];

export const Route = createFileRoute("/local-guides")({
  head: () => ({
    meta: [
      { title: "Golden Beach, Caloundra & Pelican Waters Guides | Coastside Law" },
      { name: "description", content: "Local articles, legal guides, FAQs and service pages for Golden Beach, Caloundra and Pelican Waters: property, wills, probate and business leases." },
      { property: "og:title", content: "Local guides for Golden Beach, Caloundra and Pelican Waters" },
      { property: "og:description", content: "Coastside Law's practical reading on property purchases, wills, estates, shop leases and buying a business." },
      { property: "og:type", content: "website" },
      { property: "og:url", content: url },
      { name: "twitter:card", content: "summary" },
    ],
    links: [{ rel: "canonical", href: url }],
  }),
  component: LocalGuides,
});

function LocalGuides() {
  return <>
    <PageHero eyebrow="Coastside Law · Local reading" title="Golden Beach, Caloundra & Pelican Waters" lead="Property decisions, family plans and the next step for your business." image={{ src: coastImage, alt: "Golden Beach waterfront overlooking Pumicestone Passage" }} />
    <section className="mx-auto max-w-6xl px-5 py-12">
      <h2 className="font-serif text-3xl text-foreground">Your local area</h2>
      <div className="mt-6 grid gap-8 md:grid-cols-3">
        {LOCAL_AREAS.map((area) => <div key={area.slug} className="border-t border-border pt-5">
          <h3 className="font-serif text-2xl"><Link to={area.to} className="text-primary hover:underline">{area.name} <ArrowUpRight className="inline size-4" aria-hidden="true" /></Link></h3>
          <ul className="mt-4 space-y-3 text-sm font-medium text-primary">
            <li><Link to="/conveyancing/$suburb" params={{ suburb: area.slug }} className="hover:underline">Conveyancing and contract checks</Link></li>
            <li><Link to="/wills/$suburb" params={{ suburb: area.slug }} className="hover:underline">Wills and enduring powers of attorney</Link></li>
            <li><Link to="/probate/$suburb" params={{ suburb: area.slug }} className="hover:underline">Probate and estate administration</Link></li>
            <li><Link to="/business-lawyers/$suburb" params={{ suburb: area.slug }} className="hover:underline">Business and commercial advice</Link></li>
          </ul>
        </div>)}
      </div>
    </section>
    <section className="border-y border-border bg-secondary/30">
      <div className="mx-auto max-w-6xl px-5 py-12">
        <h2 className="font-serif text-3xl text-foreground">Articles from Coastside Law</h2>
        <ul className="mt-6 grid gap-x-10 md:grid-cols-2">
          {localReadingPosts().map((post) => <li key={post.slug} className="border-b border-border py-6">
            <p className="text-xs text-muted-foreground">{post.category} · {post.area}</p>
            <h3 className="mt-2 font-serif text-xl leading-snug"><Link to="/blog/$slug" params={{ slug: post.slug }} className="text-foreground hover:text-primary hover:underline">{post.title}</Link></h3>
            <p className="mt-3 text-sm leading-6 text-muted-foreground">{post.description}</p>
          </li>)}
        </ul>
        <Link to="/blog" className="mt-6 inline-block text-sm font-semibold text-primary hover:underline">All Coastside Law articles →</Link>
      </div>
    </section>
    <section className="mx-auto grid max-w-6xl gap-12 px-5 py-12 md:grid-cols-2">
      <div>
        <h2 className="font-serif text-3xl text-foreground">In-depth guides</h2>
        <ul className="mt-5 divide-y divide-border">{guides.map((guide) => <li key={guide.slug} className="py-4"><Link to="/guides/$slug" params={{ slug: guide.slug }} className="font-medium text-primary hover:underline">{guide.label} →</Link></li>)}</ul>
        <Link to="/guides" className="mt-4 inline-block text-sm font-semibold text-primary hover:underline">All legal guides →</Link>
      </div>
      <div>
        <h2 className="font-serif text-3xl text-foreground">Common questions</h2>
        <ul className="mt-5 space-y-4 text-primary">
          <li><Link to="/faqs" className="font-medium hover:underline">Conveyancing, wills and probate FAQs →</Link></li>
          <li><Link to="/retail-shop-lease-faqs" className="font-medium hover:underline">Retail shop leases and Form 12 FAQs →</Link></li>
          <li><Link to="/will-capacity-faqs" className="font-medium hover:underline">Will capacity questions →</Link></li>
        </ul>
        <h3 className="mt-10 font-serif text-2xl">Sunshine Coast services</h3>
        <ul className="mt-4 space-y-3 text-sm text-primary">
          <li><Link to="/conveyancing-sunshine-coast" className="hover:underline">Regional conveyancing and quotes</Link></li>
          <li><Link to="/wills-estate-lawyers-sunshine-coast" className="hover:underline">Regional wills and estate planning</Link></li>
          <li><Link to="/probate" className="hover:underline">Probate and estate administration</Link></li>
          <li><Link to="/retail-shop-leases" className="hover:underline">Retail lease review</Link></li>
          <li><Link to="/business-sales" className="hover:underline">Business sales and purchases</Link></li>
        </ul>
      </div>
    </section>
  </>;
}